import { useEffect, useRef } from "react";

const isSearchInput = (element) =>
  element instanceof HTMLInputElement &&
  (element.type === "search" || /search/i.test(element.placeholder));

const isVisibleTextNode = (node) => {
  const parent = node.parentElement;
  if (!parent || !node.nodeValue.trim()) return false;
  if (["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "OPTION"].includes(parent.tagName)) {
    return false;
  }
  return parent.getClientRects().length > 0;
};

/**
 * Adds visual matches for the search input currently being used without
 * changing its filter state or the rendered result data. CSS Custom
 * Highlight ranges leave React-owned DOM untouched.
 */
const SearchHighlightManager = () => {
  const activeInput = useRef(null);
  const frame = useRef(null);

  useEffect(() => {
    if (!("highlights" in CSS) || typeof window.Highlight === "undefined") return undefined;

    const update = () => {
      frame.current = null;
      const query = activeInput.current?.value.trim();
      const highlight = new window.Highlight();

      if (!query) {
        CSS.highlights.delete("crm-search-match");
        return;
      }

      // Searches and their result tables are normally in one card. The navbar
      // search deliberately uses the document because its result list is a popup.
      const scope = activeInput.current.closest(".card") || document.body;
      const lowerQuery = query.toLocaleLowerCase();
      const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) =>
          isVisibleTextNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
      });

      let node;
      while ((node = walker.nextNode())) {
        const text = node.nodeValue;
        const lowerText = text.toLocaleLowerCase();
        let from = 0;
        let index;
        while ((index = lowerText.indexOf(lowerQuery, from)) !== -1) {
          const range = document.createRange();
          range.setStart(node, index);
          range.setEnd(node, index + query.length);
          highlight.add(range);
          from = index + query.length;
        }
      }

      CSS.highlights.set("crm-search-match", highlight);
    };

    const scheduleUpdate = () => {
      if (frame.current === null) frame.current = requestAnimationFrame(update);
    };
    const handleInput = (event) => {
      if (!isSearchInput(event.target)) return;
      activeInput.current = event.target;
      scheduleUpdate();
    };
    const observer = new MutationObserver(scheduleUpdate);

    document.addEventListener("input", handleInput);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => {
      document.removeEventListener("input", handleInput);
      observer.disconnect();
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      CSS.highlights.delete("crm-search-match");
    };
  }, []);

  return null;
};

export default SearchHighlightManager;
